import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Users as UsersIcon,
  Calendar,
  Activity,
  ArrowUpDown,
  Zap,
  AlertTriangle,
  Sparkles,
  Copy,
  Check,
  Loader2,
} from "lucide-react"
import {
  getSeasonsOverview,
  getActivityLog,
  extendSeason,
  generateSeason,
  type SeasonOverviewUser,
} from "@/lib/api"
import { toast } from "sonner"
import { cn, phoneMatches } from "@/lib/utils"
import { useReadOnly } from "@/lib/read-only"
import { SafetyDialog } from "@/components/ConfirmDialog"
import { KpiCard } from "@/components/KpiCard"
import { SeasonCoverageHeatmap } from "@/components/SeasonCoverageHeatmap"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"

type SortKey = "name" | "status" | "lastDate" | "lastSeen" | "contact" | "sport"

/** Compact relative time ("just now", "5m", "3h", "2d", "3w", "Apr 4").
 * Returns "—" when the user has never pinged a heartbeat. */
function formatLastSeen(iso?: string | null): string {
  if (!iso) return "—"
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return "—"
  const diffSec = Math.max(0, (Date.now() - then) / 1000)
  if (diffSec < 60) return "just now"
  const diffMin = diffSec / 60
  if (diffMin < 60) return `${Math.floor(diffMin)}m ago`
  const diffHr = diffMin / 60
  if (diffHr < 24) return `${Math.floor(diffHr)}h ago`
  const diffDay = diffHr / 24
  if (diffDay < 7) return `${Math.floor(diffDay)}d ago`
  if (diffDay < 28) return `${Math.floor(diffDay / 7)}w ago`
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

type SeasonStatus = "active" | "expiring" | "expired" | "none"

function getSeasonStatus(dates: Record<string, string>): SeasonStatus {
  if (!dates || Object.keys(dates).length === 0) return "none"
  const today = new Date().toISOString().split("T")[0]
  const futureDates = Object.keys(dates).filter((d) => d >= today)
  // Plan row exists but every date is in the past — the weekly cron should
  // have rolled this forward; surfaces a real lapse, not a never-generated user.
  if (futureDates.length === 0) return "expired"
  if (futureDates.length <= 3) return "expiring"
  return "active"
}

function getLastDate(dates: Record<string, string>): string {
  const sorted = Object.keys(dates || {}).sort()
  return sorted[sorted.length - 1] || ""
}

function StatusBadge({ status }: { status: SeasonStatus }) {
  const styles = {
    active:
      "bg-green-100 text-green-700 border-green-200",
    expiring:
      "bg-amber-100 text-amber-700 border-amber-200",
    expired:
      "bg-red-100 text-red-700 border-red-200",
    none: "bg-muted text-muted-foreground border-border",
  }
  const labels = {
    active: "Active",
    expiring: "Expiring",
    expired: "Expired",
    none: "No Season",
  }

  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border",
        styles[status],
      )}
    >
      {labels[status]}
    </span>
  )
}

function ContactCell({ email, phone }: { email?: string; phone?: string }) {
  const [copied, setCopied] = useState(false)
  const value = email || phone || ""
  if (!value) return <span className="text-muted-foreground">—</span>
  return (
    <button
      onClick={(e) => {
        e.stopPropagation()
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true)
          toast.success(`Copied ${value}`)
          setTimeout(() => setCopied(false), 1500)
        })
      }}
      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      title={`Click to copy ${value}`}
    >
      {copied ? (
        <Check className="size-3 text-green-600" />
      ) : (
        <Copy className="size-3 opacity-0 group-hover:opacity-50 transition-opacity" />
      )}
      <span className="truncate max-w-[200px]">{value}</span>
      {!email && phone && (
        <span className="ml-1 inline-flex items-center px-1 text-[9px] uppercase tracking-wide text-muted-foreground/70 border rounded">
          phone
        </span>
      )}
    </button>
  )
}

function SportCell({ activities }: { activities?: string[] }) {
  if (!activities || activities.length === 0)
    return <span className="text-muted-foreground">—</span>
  const primary = activities[0]
  const more = activities.length - 1
  return (
    <div className="flex items-center gap-1 flex-wrap">
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
        {primary}
      </span>
      {more > 0 && (
        <span
          className="text-[10px] text-muted-foreground"
          title={activities.slice(1).join(", ")}
        >
          +{more}
        </span>
      )}
    </div>
  )
}

export function UsersPage() {
  const navigate = useNavigate()
  const { readOnly } = useReadOnly()
  const [users, setUsers] = useState<SeasonOverviewUser[]>([])
  const [generationsLast7d, setGenerationsLast7d] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [view, setView] = useState<"list" | "coverage">("list")
  const [search, setSearch] = useState("")
  const [sortKey, setSortKey] = useState<SortKey>("name")
  const [sortAsc, setSortAsc] = useState(true)
  const [extendingIds, setExtendingIds] = useState<Set<string>>(new Set())
  const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set())
  const [confirmGenerate, setConfirmGenerate] = useState<SeasonOverviewUser | null>(null)

  const fetchUsers = async () => {
    setIsLoading(true)
    try {
      // Season overview is required; the activity log powers a header metric and
      // degrades gracefully if it fails, so it doesn't block the user table.
      const [data, log] = await Promise.all([
        getSeasonsOverview(),
        getActivityLog(200).catch(() => []),
      ])
      setUsers(data)
      const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
      setGenerationsLast7d(
        log.filter(
          (e) =>
            e.event_type === "generated" &&
            new Date(e.created_at).getTime() >= sevenDaysAgo,
        ).length,
      )
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load users",
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc)
    } else {
      setSortKey(key)
      setSortAsc(true)
    }
  }

  const handleExtend = async (e: React.MouseEvent, userId: string) => {
    e.stopPropagation()
    setExtendingIds((prev) => new Set(prev).add(userId))
    try {
      await extendSeason(userId)
      toast.success("Season extended")
      await fetchUsers()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to extend")
    } finally {
      setExtendingIds((prev) => {
        const next = new Set(prev)
        next.delete(userId)
        return next
      })
    }
  }

  const handleGenerate = async () => {
    if (!confirmGenerate) return
    const user = confirmGenerate
    const userId = user.id
    const userName = `${user.first_name} ${user.last_name}`
    // Expired users have an existing (past) plan row — backend requires
    // overwrite=true to replace it. Truly-no-plan users send false.
    const overwrite = getSeasonStatus(user.dates) === "expired"
    setGeneratingIds((prev) => new Set(prev).add(userId))
    // Close the dialog the moment the kickoff fires. The agentic worker
    // takes 7-8 min; keeping the dialog open the whole time froze both
    // Cancel and Confirm (isLoading disables them in SafetyDialog). The
    // per-row spinner below now carries the in-progress signal so the
    // rest of the UI stays interactive.
    setConfirmGenerate(null)
    toast.info(`Generating season for ${userName}… (~7–8 min)`, {
      duration: 5000,
    })
    try {
      await generateSeason(userId, overwrite)
      toast.success(`Season generated for ${userName}`)
      await fetchUsers()
    } catch (err) {
      toast.error(
        err instanceof Error
          ? `${userName}: ${err.message}`
          : `Failed to generate season for ${userName}`,
      )
    } finally {
      setGeneratingIds((prev) => {
        const next = new Set(prev)
        next.delete(userId)
        return next
      })
    }
  }

  const filtered = users.filter((u) => {
    if (!search) return true
    const haystack = [
      u.first_name,
      u.last_name,
      u.email ?? "",
      u.phone_number ?? "",
      (u.selected_activities ?? []).join(" "),
    ]
      .join(" ")
      .toLowerCase()
    return haystack.includes(search.toLowerCase()) || phoneMatches(u.phone_number, search)
  })

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortAsc ? 1 : -1
    if (sortKey === "name") {
      const aName = `${a.first_name} ${a.last_name}`
      const bName = `${b.first_name} ${b.last_name}`
      return aName.localeCompare(bName) * dir
    }
    if (sortKey === "status") {
      const order: Record<SeasonStatus, number> = {
        active: 0,
        expiring: 1,
        expired: 2,
        none: 3,
      }
      return (
        (order[getSeasonStatus(a.dates)] - order[getSeasonStatus(b.dates)]) *
        dir
      )
    }
    if (sortKey === "lastDate") {
      return getLastDate(a.dates).localeCompare(getLastDate(b.dates)) * dir
    }
    if (sortKey === "lastSeen") {
      // Never-seen (null) always sorts last regardless of direction.
      const aSeen = a.last_seen_at ? new Date(a.last_seen_at).getTime() : null
      const bSeen = b.last_seen_at ? new Date(b.last_seen_at).getTime() : null
      if (aSeen === null && bSeen === null) return 0
      if (aSeen === null) return 1
      if (bSeen === null) return -1
      return (aSeen - bSeen) * dir
    }
    if (sortKey === "contact") {
      const aC = a.email || a.phone_number || ""
      const bC = b.email || b.phone_number || ""
      return aC.localeCompare(bC) * dir
    }
    if (sortKey === "sport") {
      const aSport = (a.selected_activities ?? [])[0] ?? ""
      const bSport = (b.selected_activities ?? [])[0] ?? ""
      return aSport.localeCompare(bSport) * dir
    }
    return 0
  })

  if (isLoading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-6">Users</h1>
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-12 rounded-md bg-muted animate-pulse"
            />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Users</h1>
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center">
          <AlertTriangle className="size-8 mx-auto mb-2 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    )
  }

  // Split lapsed users by cause: never generated vs. plan exists but every date is past.
  // The expired bucket is the one the weekly cron is supposed to keep at 0.
  const usersWithoutSeasons = users.filter(
    (u) => !u.dates || Object.keys(u.dates).length === 0,
  ).length
  const usersExpired = users.filter(
    (u) => getSeasonStatus(u.dates) === "expired",
  ).length

  const statusCounts = users.reduce(
    (acc, u) => {
      acc[getSeasonStatus(u.dates)]++
      return acc
    },
    { active: 0, expiring: 0, expired: 0, none: 0 } as Record<
      SeasonStatus,
      number
    >,
  )

  const today = new Date().toISOString().split("T")[0]
  const usersWithFutureDates = users.filter((u) =>
    Object.keys(u.dates || {}).some((d) => d >= today),
  ).length
  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <UsersIcon className="size-6 text-muted-foreground" />
          <h1 className="text-2xl font-bold">Users</h1>
          <span className="text-sm text-muted-foreground">
            ({users.length}
            {usersWithoutSeasons > 0 && (
              <span className="ml-1 text-amber-700">· {usersWithoutSeasons} without seasons</span>
            )}
            {usersExpired > 0 && (
              <span
                className="ml-1 text-red-700"
                title="Plan exists but all dates are in the past — weekly cron should have rolled these forward"
              >
                · {usersExpired} expired
              </span>
            )}
            )
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          title="Total Users"
          value={users.length}
          icon={UsersIcon}
          subtitle="Users with season data"
        />
        <KpiCard
          title="Active Seasons"
          value={usersWithFutureDates}
          icon={Calendar}
          subtitle="With future workout dates"
        />
        <KpiCard
          title="AI Generations"
          value={generationsLast7d ?? "—"}
          icon={Activity}
          subtitle="Last 7 days (activity_log)"
        />
      </div>

      <Tabs value={view} onValueChange={(v) => setView(v as "list" | "coverage")}>
        <TabsList className="mb-4">
          <TabsTrigger value="list">User List</TabsTrigger>
          <TabsTrigger value="coverage">Season Coverage</TabsTrigger>
        </TabsList>

        <TabsContent value="list">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {(
          [
            { key: "active", label: "Active", cls: "bg-green-100 text-green-700 border-green-200" },
            { key: "expiring", label: "Expiring", cls: "bg-amber-100 text-amber-700 border-amber-200" },
            { key: "expired", label: "Expired", cls: "bg-red-100 text-red-700 border-red-200" },
            { key: "none", label: "No Season", cls: "bg-muted text-muted-foreground border-border" },
          ] as { key: SeasonStatus; label: string; cls: string }[]
        ).map(({ key, label, cls }) => (
          <span
            key={key}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border",
              cls,
            )}
          >
            <span className="tabular-nums font-semibold">{statusCounts[key]}</span>
            {label}
          </span>
        ))}
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Filter by name, email, or sport…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md px-3 py-2 text-sm border rounded-md bg-background focus:outline-hidden focus:ring-2 focus:ring-primary"
        />
      </div>

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left px-4 py-3 font-medium">
                <button
                  onClick={() => handleSort("name")}
                  className="flex items-center gap-1 hover:text-foreground text-muted-foreground"
                >
                  Name
                  <ArrowUpDown className="size-3" />
                </button>
              </th>
              <th className="text-left px-4 py-3 font-medium">
                <button
                  onClick={() => handleSort("contact")}
                  className="flex items-center gap-1 hover:text-foreground text-muted-foreground"
                >
                  Contact
                  <ArrowUpDown className="size-3" />
                </button>
              </th>
              <th className="text-left px-4 py-3 font-medium">
                <button
                  onClick={() => handleSort("sport")}
                  className="flex items-center gap-1 hover:text-foreground text-muted-foreground"
                >
                  Sport
                  <ArrowUpDown className="size-3" />
                </button>
              </th>
              <th className="text-left px-4 py-3 font-medium">
                <button
                  onClick={() => handleSort("status")}
                  className="flex items-center gap-1 hover:text-foreground text-muted-foreground"
                >
                  Status
                  <ArrowUpDown className="size-3" />
                </button>
              </th>
              <th className="text-left px-4 py-3 font-medium">
                <button
                  onClick={() => handleSort("lastDate")}
                  className="flex items-center gap-1 hover:text-foreground text-muted-foreground"
                >
                  Last Date
                  <ArrowUpDown className="size-3" />
                </button>
              </th>
              <th className="text-left px-4 py-3 font-medium">
                <button
                  onClick={() => handleSort("lastSeen")}
                  className="flex items-center gap-1 hover:text-foreground text-muted-foreground"
                >
                  Last Seen
                  <ArrowUpDown className="size-3" />
                </button>
              </th>
              <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                Days
              </th>
              <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((user) => {
              const status = getSeasonStatus(user.dates)
              const lastDate = getLastDate(user.dates)
              const totalDays = Object.keys(user.dates || {}).length
              const isExtending = extendingIds.has(user.id)
              const isGenerating = generatingIds.has(user.id)

              return (
                <tr
                  key={user.id}
                  onClick={() => navigate(`/users/${user.id}`)}
                  className="group border-b hover:bg-accent/50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 font-medium">
                    {user.first_name} {user.last_name}
                  </td>
                  <td className="px-4 py-3">
                    <ContactCell email={user.email} phone={user.phone_number} />
                  </td>
                  <td className="px-4 py-3">
                    <SportCell activities={user.selected_activities} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={status} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {lastDate
                      ? new Date(lastDate + "T12:00:00").toLocaleDateString(
                          "en-US",
                          { month: "short", day: "numeric" },
                        )
                      : "—"}
                  </td>
                  <td
                    className="px-4 py-3 text-muted-foreground"
                    title={
                      user.last_seen_at
                        ? new Date(user.last_seen_at).toLocaleString()
                        : "Never opened the app"
                    }
                  >
                    {formatLastSeen(user.last_seen_at)}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground tabular-nums">
                    {totalDays || "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {status === "none" || status === "expired" ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setConfirmGenerate(user)
                        }}
                        disabled={readOnly || isGenerating}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                        title={
                          readOnly
                            ? "Read-only mode"
                            : isGenerating
                              ? "Generation in progress — usually 7–8 minutes"
                              : status === "expired"
                                ? "Replace the user's expired season with a freshly generated one"
                                : "Generate the user's first workout season"
                        }
                      >
                        {isGenerating ? (
                          <Loader2 className="size-3 animate-spin" />
                        ) : (
                          <Sparkles className="size-3" />
                        )}
                        {isGenerating
                          ? "Generating…"
                          : status === "expired"
                            ? "Regenerate"
                            : "Generate"}
                      </button>
                    ) : (
                      <button
                        onClick={(e) => handleExtend(e, user.id)}
                        disabled={readOnly || isExtending}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
                        title={
                          readOnly
                            ? "Read-only mode"
                            : isExtending
                              ? "Extension in progress — usually 7–8 minutes"
                              : "Extend by one week"
                        }
                      >
                        {isExtending ? (
                          <Loader2 className="size-3 animate-spin" />
                        ) : (
                          <Zap className="size-3" />
                        )}
                        {isExtending ? "Extending…" : "Extend"}
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
            {sorted.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-8 text-center text-muted-foreground"
                >
                  {search ? "No users match your filter" : "No users found"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
        </TabsContent>

        <TabsContent value="coverage">
          <SeasonCoverageHeatmap
            users={users}
            onUserClick={(id) => navigate(`/users/${id}`)}
          />
        </TabsContent>
      </Tabs>

      {confirmGenerate && (() => {
        const isExpired = getSeasonStatus(confirmGenerate.dates) === "expired"
        return (
          <SafetyDialog
            title={`${isExpired ? "Regenerate" : "Generate"} season for ${confirmGenerate.first_name} ${confirmGenerate.last_name}`}
            description={
              isExpired
                ? "This user's previous season has fully expired (all dates are in the past). Calls the agentic pipeline to build a fresh season, replacing the lapsed one."
                : "Calls the agentic pipeline to build their first workout season. The plan is written to Supabase as soon as generation succeeds."
            }
            warnings={
              isExpired
                ? [
                    "Triggers a real Claude generation (counts toward usage).",
                    "Replaces the user's existing (expired) season — past dates will be discarded.",
                  ]
                : [
                    "Triggers a real Claude generation (counts toward usage).",
                    "If the user already has a draft, this will not overwrite it (overwrite=false).",
                  ]
            }
            confirmLabel={isExpired ? "Regenerate" : "Generate"}
            variant={isExpired ? "destructive" : "default"}
            onConfirm={handleGenerate}
            onCancel={() => setConfirmGenerate(null)}
            isLoading={generatingIds.has(confirmGenerate.id)}
          />
        )
      })()}
    </div>
  )
}
